export type HolidayCountry = "TR" | "US" | "BOTH";

export interface Holiday {
  key: string;
  country: HolidayCountry;
  isHoliday: (year: number, month: number, day: number) => boolean;
}

function nthWeekday(
  year: number,
  month: number,
  weekday: number,
  n: number,
): number {
  // weekday: 0=Sun, 1=Mon, ... 6=Sat; n is 1-based
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const offset = (weekday - firstDayOfWeek + 7) % 7;
  return 1 + offset + (n - 1) * 7;
}

function lastWeekday(year: number, month: number, weekday: number): number {
  const lastDay = new Date(year, month, 0);
  const diff = (lastDay.getDay() - weekday + 7) % 7;
  return lastDay.getDate() - diff;
}

type IslamicDates = Record<
  number,
  { ramazan: [number, number, number][]; kurban: [number, number, number][] }
>;

// Approximate dates for Ramazan Bayramı (Eid al-Fitr) and Kurban Bayramı (Eid al-Adha)
const ISLAMIC_HOLIDAYS: IslamicDates = {
  2024: {
    ramazan: [
      [2024, 4, 10],
      [2024, 4, 11],
      [2024, 4, 12],
    ],
    kurban: [
      [2024, 6, 16],
      [2024, 6, 17],
      [2024, 6, 18],
      [2024, 6, 19],
    ],
  },
  2025: {
    ramazan: [
      [2025, 3, 30],
      [2025, 3, 31],
      [2025, 4, 1],
    ],
    kurban: [
      [2025, 6, 6],
      [2025, 6, 7],
      [2025, 6, 8],
      [2025, 6, 9],
    ],
  },
  2026: {
    ramazan: [
      [2026, 3, 20],
      [2026, 3, 21],
      [2026, 3, 22],
    ],
    kurban: [
      [2026, 5, 27],
      [2026, 5, 28],
      [2026, 5, 29],
      [2026, 5, 30],
    ],
  },
  2027: {
    ramazan: [
      [2027, 3, 9],
      [2027, 3, 10],
      [2027, 3, 11],
    ],
    kurban: [
      [2027, 5, 16],
      [2027, 5, 17],
      [2027, 5, 18],
      [2027, 5, 19],
    ],
  },
  2028: {
    ramazan: [
      [2028, 2, 26],
      [2028, 2, 27],
      [2028, 2, 28],
    ],
    kurban: [
      [2028, 5, 5],
      [2028, 5, 6],
      [2028, 5, 7],
      [2028, 5, 8],
    ],
  },
};

export const HOLIDAYS: Holiday[] = [
  // ── Shared ──────────────────────────────────────────────────
  {
    key: "newYear",
    country: "BOTH",
    isHoliday: (_, m, d) => m === 1 && d === 1,
  },
  {
    key: "christmas",
    country: "BOTH",
    isHoliday: (_, m, d) => m === 12 && d === 25,
  },

  // ── Turkey ──────────────────────────────────────────────────
  {
    key: "childrenDay",
    country: "TR",
    isHoliday: (_, m, d) => m === 4 && d === 23,
  },
  {
    key: "laborDayTR",
    country: "TR",
    isHoliday: (_, m, d) => m === 5 && d === 1,
  },
  {
    key: "ataturkDay",
    country: "TR",
    isHoliday: (_, m, d) => m === 5 && d === 19,
  },
  {
    key: "democracyDay",
    country: "TR",
    isHoliday: (_, m, d) => m === 7 && d === 15,
  },
  {
    key: "victoryDay",
    country: "TR",
    isHoliday: (_, m, d) => m === 8 && d === 30,
  },
  {
    key: "republicDay",
    country: "TR",
    isHoliday: (_, m, d) => m === 10 && d === 29,
  },
  {
    key: "ramazanBayrami",
    country: "TR",
    isHoliday: (y, m, d) =>
      (ISLAMIC_HOLIDAYS[y]?.ramazan ?? []).some(
        ([dy, dm, dd]) => dy === y && dm === m && dd === d,
      ),
  },
  {
    key: "kurbanBayrami",
    country: "TR",
    isHoliday: (y, m, d) =>
      (ISLAMIC_HOLIDAYS[y]?.kurban ?? []).some(
        ([dy, dm, dd]) => dy === y && dm === m && dd === d,
      ),
  },

  // ── United States ────────────────────────────────────────────
  {
    key: "mlkDay",
    country: "US",
    // 3rd Monday of January
    isHoliday: (y, m, d) => m === 1 && d === nthWeekday(y, 1, 1, 3),
  },
  {
    key: "presidentsDay",
    country: "US",
    // 3rd Monday of February
    isHoliday: (y, m, d) => m === 2 && d === nthWeekday(y, 2, 1, 3),
  },
  {
    key: "memorialDay",
    country: "US",
    // Last Monday of May
    isHoliday: (y, m, d) => m === 5 && d === lastWeekday(y, 5, 1),
  },
  {
    key: "juneteenth",
    country: "US",
    isHoliday: (_, m, d) => m === 6 && d === 19,
  },
  {
    key: "independenceDay",
    country: "US",
    isHoliday: (_, m, d) => m === 7 && d === 4,
  },
  {
    key: "laborDayUS",
    country: "US",
    // 1st Monday of September
    isHoliday: (y, m, d) => m === 9 && d === nthWeekday(y, 9, 1, 1),
  },
  {
    key: "columbusDay",
    country: "US",
    // 2nd Monday of October
    isHoliday: (y, m, d) => m === 10 && d === nthWeekday(y, 10, 1, 2),
  },
  {
    key: "veteransDay",
    country: "US",
    isHoliday: (_, m, d) => m === 11 && d === 11,
  },
  {
    key: "thanksgiving",
    country: "US",
    // 4th Thursday of November
    isHoliday: (y, m, d) => m === 11 && d === nthWeekday(y, 11, 4, 4),
  },
];

export function getTodayHolidays(date: Date): Holiday[] {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return HOLIDAYS.filter((h) => h.isHoliday(year, month, day));
}
